import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../variables/api/axiosInstance";
import { displayNumbers } from "../../variables/numbersFunctions";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import Footer from "../../components/Footer";
import ActionDropdown from "../production/components/ActionDropdown";
import { LOGGED_USER } from "../../variables/constants";

const { Title, Text } = Typography;

const CAT_COLORS = { MOTO: "blue", AUTO: "orange", CAMION: "purple", BUS: "cyan" };
const getCatColor = (nom) => CAT_COLORS[nom?.toUpperCase()] || "default";

// Droit d'ÉDITION : dépend de l'organisme de la GARANTIE parente (Pool = tout ; compagnie =
// ses propres garanties ; garanties générales = lecture seule pour les non-Pool).
const _isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
const _userOrgCode = LOGGED_USER?.profil?.organisme?.code;
const canEditGarantie = (g) => {
  if (_isPoolUser) return true;
  const org = g?.organisme;
  if (!org || org.typeOrganisme === "POOL") return false;
  return !!_userOrgCode && org.code === _userOrgCode;
};

const SousGarantiesV2 = () => {
  const [sousGaranties, setSousGaranties] = useState([]);
  const [garantie, setGarantie] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSousGarantie, setSelectedSousGarantie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const param = useParams();
  const navigate = useNavigate();

  const fetchSousGaranties = () => {
    axiosInstance
      .get(`/api/sous-garantie/garantie/all/${param.garantieId}`)
      .then((res) => setSousGaranties(res.data))
      .catch(() => toast.error("Erreur lors de la récupération des sous-garanties"));
  };

  const fetchGarantie = () => {
    axiosInstance
      .get(`/api/garantie/codeId/${param.garantieId}`)
      .then((res) => setGarantie(res.data))
      .catch(() => toast.error("Erreur lors de la récupération de la garantie"));
  };

  useEffect(() => {
    fetchSousGaranties();
    fetchGarantie();
  }, [param.garantieId]);

  const openAdd = () => {
    setEditMode(false);
    setSelectedSousGarantie(null);
    form.resetFields();
    form.setFieldsValue({
      obligatoire: false,
      prorogation: false,
      primeNette: 0,
      tauxPCent: 0,
      tauxPMille: 0,
      trancheMin: 0,
      trancheMax: 0,
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditMode(true);
    setSelectedSousGarantie(record);
    form.setFieldsValue({
      nom: record.nom,
      commentaire: record.commentaire,
      obligatoire: record.obligatoire || false,
      prorogation: record.prorogation || false,
      primeNette: record.primeNette || 0,
      tauxPCent: record.tauxPCent || 0,
      tauxPMille: record.tauxPMille || 0,
      trancheMin: record.tranche?.[0] || 0,
      trancheMax: record.tranche?.[1] || 0,
    });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    form.resetFields();
    setSelectedSousGarantie(null);
  };

  const handleSubmit = (values) => {
    setSubmitting(true);
    const payload = {
      nom: values.nom,
      commentaire: values.commentaire || "",
      obligatoire: values.obligatoire || false,
      prorogation: values.prorogation || false,
      primeNette: values.primeNette || 0,
      tauxPCent: values.tauxPCent || 0,
      tauxPMille: values.tauxPMille || 0,
      tranche:
        values.trancheMin || values.trancheMax
          ? [values.trancheMin || 0, values.trancheMax || 0]
          : [],
      garantie,
    };
    if (editMode && selectedSousGarantie?.codeId) {
      payload.codeId = selectedSousGarantie.codeId;
    }

    const req = editMode
      ? axiosInstance.put("/api/sous-garantie", payload)
      : axiosInstance.post("/api/sous-garantie", payload);

    req
      .then(() => {
        toast.success(editMode ? "Sous-garantie modifiée avec succès" : "Sous-garantie ajoutée avec succès");
        handleModalClose();
        fetchSousGaranties();
      })
      .catch(() => toast.error("Erreur lors de l'enregistrement"))
      .finally(() => setSubmitting(false));
  };

  const confirmDelete = (codeId) => {
    Modal.confirm({
      title: "Supprimer cette sous-garantie ?",
      icon: <ExclamationCircleFilled style={{ color: "#ff4d4f" }} />,
      content: "Cette action est irréversible.",
      okText: "Supprimer",
      okButtonProps: { danger: true },
      cancelText: "Annuler",
      onOk() {
        axiosInstance
          .delete(`/api/sous-garantie/code/${codeId}`)
          .then(() => {
            toast.success("Sous-garantie supprimée");
            fetchSousGaranties();
          })
          .catch(() => toast.error("Erreur lors de la suppression"));
      },
    });
  };

  const filteredSousGaranties = sousGaranties.filter(
    (sg) => !searchText || sg.nom?.toLowerCase().includes(searchText.toLowerCase())
  );

  const catColor = getCatColor(garantie?.categorie?.nom);
  // Éditable seulement si l'utilisateur a le droit sur la garantie parente.
  const editable = canEditGarantie(garantie);

  const columns = [
    {
      title: "Sous-garantie",
      dataIndex: "nom",
      key: "nom",
      render: (nom, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text strong>{nom}</Text>
            {record.tranche?.[1] ? (
              <Tag color="geekblue" style={{ fontSize: 10 }}>
                {record.tranche[0]} – {record.tranche[1]} CV
              </Tag>
            ) : null}
          </Space>
          {record.commentaire && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.commentaire}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Obligatoire",
      dataIndex: "obligatoire",
      key: "obligatoire",
      align: "center",
      width: 110,
      render: (ob) =>
        ob ? (
          <Tag icon={<CheckCircleFilled />} color="success">
            Oui
          </Tag>
        ) : (
          <Tag icon={<CloseCircleFilled />} color="default">
            Non
          </Tag>
        ),
    },
    {
      title: "Taux",
      key: "taux",
      align: "center",
      width: 120,
      render: (_, record) =>
        record.tauxPCent ? (
          <Tag color="purple">{record.tauxPCent} %</Tag>
        ) : record.tauxPMille ? (
          <Tag color="blue">{record.tauxPMille} ‰</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Prime nette",
      dataIndex: "primeNette",
      key: "primeNette",
      align: "right",
      width: 140,
      render: (val) =>
        val > 0 ? (
          <Text strong style={{ color: "#52c41a" }}>
            {displayNumbers(val)} F
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Prorogation",
      dataIndex: "prorogation",
      key: "prorogation",
      align: "center",
      width: 110,
      render: (p) =>
        p ? <Tag color="orange">Oui</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) =>
        editable ? (
          <ActionDropdown
            actions={[
              {
                key: "edit",
                label: "Modifier",
                icon: <EditOutlined />,
                onClick: () => openEdit(record),
              },
              {
                key: "delete",
                label: "Supprimer",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => confirmDelete(record.codeId),
              },
            ]}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>Lecture seule</Text>
        ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"TARIFICATION"} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* Breadcrumb */}
              <Breadcrumb
                style={{ marginBottom: 16 }}
                items={[
                  { title: "Paramètres" },
                  {
                    title: (
                      <span
                        style={{ cursor: "pointer", color: "#667eea" }}
                        onClick={() => navigate("/garanties")}
                      >
                        Garanties
                      </span>
                    ),
                  },
                  { title: garantie?.nom || "Sous-garanties" },
                ]}
              />

              {/* Header with parent garantie info */}
              <div
                style={{
                  background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                  borderRadius: 12,
                  padding: "20px 28px",
                  marginBottom: 24,
                  color: "#fff",
                }}
              >
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space align="center">
                      <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate("/garanties")}
                        style={{
                          background: "rgba(255,255,255,0.2)",
                          border: "none",
                          color: "#fff",
                          borderRadius: 8,
                        }}
                      />
                      <div>
                        <Space align="center" style={{ marginBottom: 2 }}>
                          <Title level={4} style={{ color: "#fff", margin: 0 }}>
                            {garantie?.nom || "—"}
                          </Title>
                          {garantie?.categorie?.nom && (
                            <Tag
                              color={catColor}
                              style={{ borderRadius: 20, fontWeight: 600 }}
                            >
                              {garantie.categorie.nom}
                            </Tag>
                          )}
                          {garantie?.obligatoire && (
                            <Tag color="red" style={{ borderRadius: 20 }}>
                              Obligatoire
                            </Tag>
                          )}
                        </Space>
                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                          {garantie?.commentaire || "Aucune description"}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  {editable && (
                    <Col>
                      <Button
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={openAdd}
                        style={{
                          background: "#fff",
                          color: "#11998e",
                          border: "none",
                          fontWeight: 600,
                          borderRadius: 8,
                        }}
                      >
                        Nouvelle sous-garantie
                      </Button>
                    </Col>
                  )}
                </Row>
              </div>

              {/* Stats */}
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8} md={6}>
                  <Card bordered={false} style={{ borderRadius: 10, textAlign: "center" }}>
                    <Statistic
                      title="Total"
                      value={sousGaranties.length}
                      prefix={<SafetyCertificateOutlined />}
                      valueStyle={{ color: "#11998e" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Card bordered={false} style={{ borderRadius: 10, textAlign: "center" }}>
                    <Statistic
                      title="Obligatoires"
                      value={sousGaranties.filter((sg) => sg.obligatoire).length}
                      prefix={<CheckCircleFilled />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Table card */}
              <Card
                bordered={false}
                style={{ borderRadius: 12 }}
                styles={{ body: { padding: 0 } }}
              >
                <div style={{ padding: "16px 24px" }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text strong style={{ fontSize: 15 }}>
                        Sous-garanties de «{garantie?.nom || "..."}»
                      </Text>
                    </Col>
                    <Col>
                      <Input
                        prefix={<SearchOutlined />}
                        placeholder="Rechercher..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 220, borderRadius: 8 }}
                        allowClear
                      />
                    </Col>
                  </Row>
                </div>
                <Divider style={{ margin: 0 }} />
                <Table
                  columns={columns}
                  dataSource={filteredSousGaranties}
                  rowKey="codeId"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `${total} sous-garantie(s)`,
                  }}
                />
              </Card>
            </div>
            <Footer />
            <div className="content-backdrop fade"></div>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal
        open={modalOpen}
        onCancel={handleModalClose}
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: "#11998e" }} />
            <span>{editMode ? "Modifier la sous-garantie" : "Nouvelle sous-garantie"}</span>
          </Space>
        }
        footer={null}
        width={640}
        destroyOnClose
      >
        <Divider style={{ margin: "8px 0 20px" }} />
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item
                name="nom"
                label="Libellé"
                rules={[{ required: true, message: "Le libellé est requis" }]}
              >
                <Input placeholder="Ex: Défense et recours" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="primeNette" label="Prime nette (F CFA)">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="tauxPCent" label="Taux (%)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} addonAfter="%" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tauxPMille" label="Taux (‰)">
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} addonAfter="‰" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="trancheMin" label="Tranche min (CV)">
                <InputNumber style={{ width: "100%" }} min={0} addonAfter="CV" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="trancheMax" label="Tranche max (CV)">
                <InputNumber style={{ width: "100%" }} min={0} addonAfter="CV" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="commentaire" label="Description">
            <Input.TextArea rows={2} placeholder="Description optionnelle..." />
          </Form.Item>

          <Row gutter={32}>
            <Col>
              <Form.Item name="obligatoire" label="Obligatoire" valuePropName="checked">
                <Switch checkedChildren="OUI" unCheckedChildren="NON" />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="prorogation" label="Prorogation" valuePropName="checked">
                <Switch checkedChildren="OUI" unCheckedChildren="NON" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={handleModalClose}>Annuler</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                style={{
                  background: editMode
                    ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                    : "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                }}
              >
                {editMode ? "Enregistrer les modifications" : "Créer la sous-garantie"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default SousGarantiesV2;
